import { useState } from "react";
import { Loader2, RefreshCw } from "lucide-react";

import { Button } from "components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "components/ui/dialog";
import { toast } from "components/ui/use-toast";
import {
  useGetJobMetaQuery,
  useGetReprocessStatusQuery,
  useReprocessTaxClosingMutation,
} from "@/services/tax-closing";

import { TAX_CLOSING_TOASTS } from "../messages";

const REPROCESS_STATUS_POLL_MS = 15000;

interface ReprocessOfmButtonProps {
  jobId: string | undefined;
}

/**
 * Reprocessamento OFM — disponível apenas nesta tela de detalhes.
 * Recupera mês/ano/lojas do job (via /meta) e dispara o reprocessamento
 * com modal de confirmação + dedupe + detecção de pendência ao carregar.
 */
export function ReprocessOfmButton({ jobId }: ReprocessOfmButtonProps) {
  const [confirmOpen, setConfirmOpen] = useState(false);

  const { data: meta } = useGetJobMetaQuery(jobId!, { skip: !jobId });
  const month = meta?.month ?? null;
  const year = meta?.year ?? null;
  const stores = meta?.stores ?? [];
  const hasParams = !!month && !!year && stores.length > 0;

  const { data: reprocessStatus } = useGetReprocessStatusQuery(
    { year: year as number, month: month as number },
    { skip: !hasParams },
  );
  const isPending = reprocessStatus?.pending ?? false;

  // Enquanto houver reprocessamento em andamento, mantém o status em polling
  // pra o botão reabilitar automaticamente quando os jobs terminarem (sem
  // depender de refresh manual). Para de pollar assim que deixa de estar
  // pendente. Mesmo padrão usado em SchedulerJobs.
  useGetReprocessStatusQuery(
    { year: year as number, month: month as number },
    {
      skip: !hasParams || !isPending,
      pollingInterval: REPROCESS_STATUS_POLL_MS,
    },
  );
  const [reprocess, { isLoading }] = useReprocessTaxClosingMutation();

  const handleConfirm = async () => {
    if (!hasParams) return;
    try {
      const result = await reprocess({
        year: year as number,
        month: month as number,
        lojas: stores,
        fallback: "ofm",
      }).unwrap();
      if (result.alreadyPending) {
        toast(TAX_CLOSING_TOASTS.reprocessAlreadyPending());
      } else {
        toast(TAX_CLOSING_TOASTS.reprocessRequested(stores.length, result.id));
      }
    } catch (err) {
      const description =
        (err as { data?: { message?: string } })?.data?.message ?? String(err);
      toast(TAX_CLOSING_TOASTS.reprocessError(description));
    } finally {
      setConfirmOpen(false);
    }
  };

  if (!jobId) return null;

  return (
    <>
      <Button
        variant="outline"
        className="gap-2"
        disabled={!hasParams || isLoading || isPending}
        onClick={() => setConfirmOpen(true)}
      >
        {isLoading || isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <RefreshCw className="h-4 w-4" />
        )}
        {isPending ? "Reprocessamento em andamento…" : "Reprocessar OFM"}
      </Button>

      <Dialog
        open={confirmOpen}
        onOpenChange={(o) => !o && setConfirmOpen(false)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Reprocessar dados OFM</DialogTitle>
            <DialogDescription>
              Será solicitado o reprocessamento OFM de{" "}
              <span className="font-semibold text-foreground">
                {stores.length} loja(s)
              </span>
              {month && year
                ? ` para ${String(month).padStart(2, "0")}/${year}`
                : ""}
              . Esse processo pode demorar alguns minutos para ser concluído.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2 sm:gap-2">
            <Button
              variant="outline"
              onClick={() => setConfirmOpen(false)}
              disabled={isLoading}
            >
              Cancelar
            </Button>
            <Button onClick={handleConfirm} disabled={isLoading}>
              {isLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Confirmar reprocessamento
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
